// pages/ContributorWritePage.jsx — Anamoria SPA
// v1.10 — NEW-163 Increment B (2026-10-30): a member can respond to the leader's note.
//   - Reads `?note=<group_notes id>` from location.search (it is in the URL, so it survives refresh and the
//     voice/video round trip) and quotes the note at the top of the capture + review screens
//     (RespondingToNote; copy v1.0 B-8). The note is resolved from space.groupNotes already loaded here.
//   - handleSave: the create payload carries `groupNoteId` only when the id resolves against
//     space.groupNotes (unknown → untagged; the server validates regardless, 400 INVALID_GROUP_NOTE).
//   - Share to the group with a loved-one name from ContributorSaveActions v1.4 (opts.lovedOneName):
//     PATCH {prefix}/{spaceId} { lovedOneName } (the live body-action) BEFORE the create so the
//     attribution join finds it on first render. If that PATCH fails nothing is created (the page's
//     standard error; the member can retry). Non-group / flag-off: payload byte-identical to v1.9.
//   SPA build spec v0.2 §6 (C-e confirmed at build: single create body, space GET on mount).
// E3b (Consent PR E, Oct 4, 2026): edit mode passes space/api/keepCopyExcluded to ContributorEditSharing;
//   handleKeepCopy PATCHes keepCopyExcluded (+ keepCopyShownHash on re-include); share state carries keepCopyExcluded.
// E3a (Consent PR E, Oct 4, 2026): handleSave(visibilityPref, keepCopyOpts) forwards keepCopy + keepCopyShownHash
//   from ContributorSaveActions on a first share; NOTICE_CHANGED/NOTICE_UNAVAILABLE -> plain error. Dark while the flag is off.
// v1.9 — W3 inc2 (Sep 7, 2026): gate text create + edit on space.textAsObjectEnabled. Flag on →
//   presign text/plain + PUT words to S3 + send textContentKey (edit re-uploads to the memory's own
//   key via memoryId); flag off → inline note, byte-identical. Mirrors owner AddMemoryPage/
//   MemoryDetailPage. Plan: W3 Increment2 ContributorSPA Options Plan v0.2 §2b.
// v1.8 — PR-7a.2: edit mode uses the shared compact Cancel/Save-changes footer and saves directly
//   (no Review step in edit — Review stays only for creating a new memory). (2026-09-05)
// v1.7 — PR-7a.1: sharing UX clarity. Sharing saves immediately; if content edits are unsaved the user
//   stays (nudged), otherwise a toast confirms and drops back to the feed. Edit-mode primary button reads
//   'Save changes'. Text sharing controls moved onto the compose (landing) screen. (2026-09-05)
// v1.6 — PR-7a / NS-6: edit mode now shows sharing controls (ContributorEditSharing):
//   change tier / share / make-private on the contributor's own memory, via a visibility-only
//   PATCH (independent of the content save). Group-gated. Copy = Copy v0.4 + v0.7 Stop-sharing set;
//   pull-back is silent (PR-4b). (2026-09-05)
// v1.5 — PR-6 / NEW-64 private-by-default: "Save just for me" now saves a
//   PRIVATE (contributor_only) memory in THIS space for EVERY contributor via
//   the contributor create pipeline (handleSave('contributor_only')) — no
//   own-space copy. Removed the own-space owner pipeline (handleSaveJustForMe,
//   ownerApi/createApiClient) and the ownSpaceId gating on the button.
//   ContributorSaveActions now takes onSavePrivate. (2026-09-04)
// v1.4 — B1b: group-spaces two-button save (dark). When space.groupSpacesEnabled,
//   the review screen shows "Save just for me" (private draft → own space via the
//   owner create pipeline, linked only) + "Share this memory" (scope picker →
//   visibilityPref owner_contributor_only|group on the contributor create). Flag
//   OFF or edit mode → unchanged single "Save memory" button (byte-identical).
//   New shared <ContributorSaveActions>. Copy = Contributor Sharing Options v0.7.
//   (July 26, 2026)
// v1.3 — Header + prompt banner fix: use shared LovedOneBar/PromptBanner (May 31, 2026)
//   Root cause: this page hand-rolled its header and prompt banner against
//   WritePage.module.css classes (.lovedOneBar/.bar*/.promptBanner/...) that
//   were REMOVED from WritePage.module.css in v1.2 and moved into the shared
//   <LovedOneBar> and <PromptBanner> components — which the owner WritePage was
//   migrated to, but this page never was. Those classes resolved to undefined
//   here, so the header/prompt rendered unstyled (the "broken header").
//   Fix: render the SAME shared components the owner uses.
//     - Compose + review headers → <LovedOneBar> (same props as owner).
//     - Generic prompt → <PromptBanner fullWidth showSkip={false}>, moved
//       outside .composeContent to match owner layout (still gated on !isEditMode).
//     - Removed now-unused spaceInitial (LovedOneBar derives its own initial).
//   Scope: ONLY this file. Owner WritePage.jsx, WritePage.module.css, and the
//   shared components are untouched. No data/save/navigation/logic change.
//
// v1.2 — Account-link access mode (May 31, 2026)
//   - Added `mode` prop ('session' default → no regression). Data layer from
//     getContributorAccess(mode, getAccessTokenSilently); contribute paths use
//     the bound prefix; navigation + expiry are mode/prefix-aware; linked mode
//     uses no sessionStorage token. The session-mode "Go back" target remains
//     /contribute/:spaceId (ContributorHomePage, session-only).
//
// v1.1 — Edit mode for text memories (May 25, 2026)
//
// Changes from v1.0:
//   - Reads location.state.editMemory on mount. If present, pre-populates
//     title and text fields, hides prompt banner, changes subtitle to
//     "Edit memory", and saves via PATCH instead of POST.
//   - No post-save gating in edit mode — straight back to feed.
//
// v1.0 — Session 3 (April 19, 2026)
//
// Contributor text capture page. Parallel to owner WritePage.jsx v1.4 but
// adapted for contributor context.
//
// Route: /contribute/:spaceId/write
//
// CSS: Reuses WritePage.module.css per Session 3 Plan v1.1 decision B2
// (new component, shared CSS module). Zero CSS duplication.
//
// Differences from owner WritePage.jsx v1.4:
//   - Auth: createContributorApiClient (session token) instead of Auth0 JWT
//   - Space fetch: GET /contribute/:spaceId instead of /spaces/:id
//   - Save: POST /contribute/:spaceId/memories with {type:'text', title, note}
//     (no isPrivate field — Lambda hardcodes FALSE for contributor memories
//     per Session 3 Plan v1.1 decision — contributors always share)
//   - Prompts: hardcoded generic "Share a memory of {spaceName}" per
//     Session 3 decision P2. Backend has no /contribute/:spaceId/prompt
//     route, and adding one is not in scope for Session 3. No
//     prompt/advance or prompt/respond calls.
//   - No "Try a different prompt" skip link — there's no prompt to advance.
//   - No privacy toggle in review screen — contributor memories always shared.
//   - No SuccessScreen after save — shows a saved pill toast per Session 3
//     decision S2+Q2-revised, then navigates to /contribute/:spaceId/memories.
//     Rationale: avoids SuccessScreen bottom-nav ownership issue (RK-4) and
//     matches existing toast pattern from InvitePage v1.1 session log.
//   - No post-save gating (reminder/feedback) — contributor flow has none.
//   - Cancel button navigates to /contribute/:spaceId/memories (feed).
//
// Structure preserved from owner:
//   compose → review → save → navigate (no separate success step)
//   Title input + textarea + DictateButton
//   Character counter (10,000 limit same as owner)
//   Review preview before save
//
// DictateButton: included per Session 3 Plan v1.1 OI-1 default.
// Same component as owner; uses browser SpeechRecognition, no backend call.

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { getContributorAccess } from '../api/contributorAccess';
import LovedOneBar from '../components/LovedOneBar';
import PromptBanner from '../components/PromptBanner';
import DictateButton from '../components/DictateButton';
import ContributorSaveActions from '../components/ContributorSaveActions';
import RespondingToNote from '../components/RespondingToNote';   // v1.10 (NEW-163)
import { readNoteParam, findGroupNote } from '../utils/contributorCapture';   // v1.10 (NEW-163)
import ContributorEditSharing from '../components/ContributorEditSharing';
import ContributorEditActions from '../components/ContributorEditActions';
import styles from './WritePage.module.css';

const MAX_CHARS = 10000;

// Toast dismiss duration — matches InvitePage v1.1 toast pattern (~3s).
const TOAST_DURATION_MS = 2500;

/* ═══════════════════════════════════════
   CONTRIBUTOR WRITE PAGE
   ═══════════════════════════════════════ */

export default function ContributorWritePage({ mode = 'linked' }) {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // v1.10 (NEW-163): the group note being responded to, from ?note= (null when absent).
  const groupNoteId = readNoteParam(new URLSearchParams(location.search || ''));
  const { getAccessTokenSilently } = useAuth0();

  const navBase = '/contributing';
  const expiredMsg = 'Your session has expired. Please sign in again.';
  const access = useMemo(
    () => getContributorAccess(mode, getAccessTokenSilently),
    [mode, getAccessTokenSilently]
  );

  // v1.1: Edit mode — pre-populate from existing memory
  const editMemory = location.state?.editMemory || null;
  const isEditMode = !!editMemory;

  // Space data
  const [space, setSpace] = useState(null);
  // Form state
  const [title, setTitle] = useState(editMemory?.title || '');
  const [text, setText] = useState(editMemory?.note || '');
  // UI state
  const [step, setStep] = useState('compose'); // 'compose' | 'review'
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Saved pill state (toast, auto-dismiss then navigate)
  const [showSavedPill, setShowSavedPill] = useState(false);

  const textareaRef = useRef(null);
  const toastTimerRef = useRef(null);

  /* ─── Fetch space on mount ─── */
  useEffect(() => {
    // Track B (B-2): linked-only; auth via Auth0 JWT behind ProtectedRoute.
    let cancelled = false;
    async function load() {
      try {
        const data = await access.api.get(`${access.prefix}/${spaceId}`);
        if (cancelled) return;
        setSpace(data);
      } catch (err) {
        if (cancelled) return;
        if (err.error === 'INVALID_SESSION' || err.error === 'NO_SESSION_TOKEN' || err.status === 401) {
          setError(expiredMsg);
        } else {
          setError('Failed to load. Please go back and try again.');
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [spaceId, access, expiredMsg]);

  /* ─── Focus textarea when entering compose ─── */
  useEffect(() => {
    if (step === 'compose' && textareaRef.current) {
      const t = setTimeout(() => textareaRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [step]);

  /* ─── Cleanup toast timer on unmount (prevents setState on unmounted component) ─── */
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  /* ─── Handlers ─── */

  const handleTextChange = useCallback((e) => {
    const val = e.target.value;
    if (val.length <= MAX_CHARS) {
      setText(val);
    }
  }, []);

  const handleTitleChange = useCallback((e) => {
    setTitle(e.target.value);
  }, []);

  const handleDictation = useCallback((transcript) => {
    setText((prev) => {
      const separator = prev && !prev.endsWith(' ') ? ' ' : '';
      const next = prev + separator + transcript;
      return next.length <= MAX_CHARS ? next : next.substring(0, MAX_CHARS);
    });
  }, []);

  const handleCancel = useCallback(() => {
    navigate(`${navBase}/${spaceId}/memories`);
  }, [navigate, navBase, spaceId]);

  const handleReview = useCallback(() => {
    if (!text.trim()) return;
    setStep('review');
  }, [text]);

  const handleBackToCompose = useCallback(() => {
    setStep('compose');
  }, []);

  // E3a: keepCopyOpts = { keepCopy: true, keepCopyShownHash } from the share picker (first share), else undefined.
  const handleSave = useCallback(async (visibilityPref, keepCopyOpts) => {
    if (saving) return;
    setSaving(true);
    setError(null);

    try {
      // v1.10 (NEW-163): resolve the note being responded to against the space's notes; unknown → untagged.
      const respondNoteId = (!isEditMode && groupNoteId && space?.isGroupSpace === true && space?.groupSpacesEnabled === true && findGroupNote(space.groupNotes, groupNoteId)) ? groupNoteId : null;
      // v1.10 (NEW-163): loved-one name entered on the share step → save it BEFORE the create (body-action
      // PATCH on the contributor space route), so "remembering {name}" is there on first render.
      if (!isEditMode && visibilityPref === 'group' && typeof keepCopyOpts?.lovedOneName === 'string') {
        await access.api.patch(`${access.prefix}/${spaceId}`, { lovedOneName: keepCopyOpts.lovedOneName });
      }
      const api = access.api;
      // W3 inc2: when text_as_object_enabled is on for this space, write the words as an S3
      // object (Option A) — presign text/plain, PUT the words, send textContentKey — and the
      // backend reads them back + hashes. Flag off → inline note, byte-identical to today.
      // (This page is text-only; photos/voice have their own contributor pages.) Legacy inline
      // memories are converted by the one-time backfill before the flag is on, so an edit under
      // flag-on always finds an existing object key to re-hash (matches owner MemoryDetailPage).
      const textAsObjectEnabled = space?.textAsObjectEnabled === true;

      if (isEditMode) {
        // v1.1 / v1.9: PATCH existing memory
        const patch = { title: title.trim() || null };
        if (textAsObjectEnabled) {
          const up = await api.post(`${access.prefix}/${spaceId}/upload-url`, { mediaType: 'text', mimeType: 'text/plain', memoryId: editMemory.id });
          await api.putS3(up.uploadUrl, new Blob([text.trim()], { type: 'text/plain' }), 'text/plain');
          patch.textContentKey = up.s3Key;
        } else {
          patch.note = text.trim();
        }
        await api.patch(`${access.prefix}/${spaceId}/memories/${editMemory.id}`, patch);
      } else {
        // Create new memory
        const body = {
          type: 'text',
          title: title.trim() || null,
          // B1b (dark): send the chosen share tier only when group spaces is ON;
          // omitted entirely otherwise → payload byte-identical to today.
          ...(space?.groupSpacesEnabled && typeof visibilityPref === 'string' ? { visibilityPref } : {}),
          ...(keepCopyOpts?.keepCopy === true && keepCopyOpts.keepCopyShownHash ? { keepCopy: true, keepCopyShownHash: keepCopyOpts.keepCopyShownHash } : {}),   // E3a
          ...(respondNoteId ? { groupNoteId: respondNoteId } : {}),   // v1.10 (NEW-163): only when the id resolves in space.groupNotes
        };
        if (textAsObjectEnabled) {
          const up = await api.post(`${access.prefix}/${spaceId}/upload-url`, { mediaType: 'text', mimeType: 'text/plain' });
          await api.putS3(up.uploadUrl, new Blob([text.trim()], { type: 'text/plain' }), 'text/plain');
          body.textContentKey = up.s3Key;
        } else {
          body.note = text.trim();
        }
        await api.post(`${access.prefix}/${spaceId}/memories`, body);
      }

      // Show saved pill, then navigate after dismissal.
      setSaving(false);
      setShowSavedPill(true);
      toastTimerRef.current = setTimeout(() => {
        navigate(`${navBase}/${spaceId}/memories`, { replace: true });
      }, TOAST_DURATION_MS);
    } catch (err) {
      console.error('Save error:', err);
      if (err.error === 'INVALID_SESSION' || err.error === 'NO_SESSION_TOKEN' || err.status === 401) {
        setError(expiredMsg);
      } else if (err.error === 'NOTICE_CHANGED' || err.error === 'NOTICE_UNAVAILABLE') {
        // E3a: the keep-a-copy wording changed while they were reading (server rolled the save back).
        setError("The wording for keeping a copy has changed. Nothing was saved. Please go back and read it again.");
      } else {
        setError("Something didn't save. Please try again.");
      }
      setSaving(false);
    }
  }, [saving, text, title, spaceId, navigate, navBase, isEditMode, editMemory, access, expiredMsg, space, groupNoteId]);

  // PR-7a: edit-mode sharing state (seeded from the memory being edited; updated from the PATCH response).
  const [share, setShare] = useState({ visibilityPref: editMemory?.visibilityPref, reviewStatus: editMemory?.reviewStatus, keepCopyExcluded: editMemory?.keepCopyExcluded });   // E3b
  const [sharingBusy, setSharingBusy] = useState(false);
  const [sharingNotice, setSharingNotice] = useState(false);
  const contentDirty = (text !== (editMemory?.note || '')) || (title !== (editMemory?.title || ''));
  const applySharing = async (body, failMsg) => {
    if (!editMemory) return;
    setSharingBusy(true); setError(null); setSharingNotice(false);
    try {
      const res = await access.api.patch(`${access.prefix}/${spaceId}/memories/${editMemory.id}`, body);
      setShare((prev) => ({ visibilityPref: res.visibilityPref, reviewStatus: res.reviewStatus, keepCopyExcluded: res.keepCopyExcluded ?? prev.keepCopyExcluded }));   // E3b: response carries keepCopyExcluded only after a change
      if (contentDirty) {
        setSharingNotice(true); // keep them here so unsaved content edits aren't lost
      } else {
        setShowSavedPill(true);
        toastTimerRef.current = setTimeout(() => {
          navigate(`${navBase}/${spaceId}/memories`, { replace: true });
        }, TOAST_DURATION_MS);
      }
    } catch (err) {
      if (err.error === 'INVALID_SESSION' || err.error === 'NO_SESSION_TOKEN' || err.status === 401) setError(expiredMsg);
      else if (err.error === 'NOTICE_CHANGED' || err.error === 'NOTICE_UNAVAILABLE') setError("The wording for keeping a copy has changed. Nothing was saved. Please go back and read it again.");   // E3b
      else setError(failMsg);
    } finally { setSharingBusy(false); }
  };
  const handleShareTier = (vp) => applySharing({ visibilityPref: vp }, "We weren't able to update who can see this. When you're ready, please try again.");
  const handleMakePrivate = () => applySharing({ makePrivate: true }, "We weren't able to stop sharing that memory. When you're ready, please try again.");
  const handleKeepCopy = (excluded, shownHash) => applySharing(excluded ? { keepCopyExcluded: true } : { keepCopyExcluded: false, keepCopyShownHash: shownHash }, "We weren't able to change that. When you're ready, please try again.");   // E3b

  /* ─── Derived values ─── */
  const charCount = text.length;
  const nearLimit = charCount > 9000;
  const canReview = text.trim().length > 0;
  const spaceName = space?.spaceName || 'this space';
  // v1.10 (NEW-163): the quoted leader note (B-8) when responding; null otherwise / in edit mode.
  const respondQuote = (!isEditMode && groupNoteId && space?.isGroupSpace === true && space?.groupSpacesEnabled === true) ? <RespondingToNote groupNotes={space?.groupNotes} noteId={groupNoteId} leaderName={space?.ownerName} /> : null;

  /* ─── Loading state ─── */
  if (!space && !error) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingDot} />
        <span>Loading...</span>
      </div>
    );
  }

  /* ─── Error state (pre-load) ─── */
  if (error && !space) {
    return (
      <div className={styles.errorScreen}>
        <p>{error}</p>
        <button
          className={styles.btnPrimary}
          onClick={() => navigate(`${navBase}/${spaceId}/memories`)}
        >
          Go back
        </button>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════
     REVIEW SUB-SCREEN
     ═══════════════════════════════════════════════════ */
  if (step === 'review') {
    return (
      <div className={styles.reviewScreen}>

        {/* v1.3: shared LovedOneBar (was dead WritePage.module.css classes).
            Back-to-compose matches the owner review header. */}
        <LovedOneBar
          spaceName={spaceName}
          spacePhotoUrl={space.photoUrl}
          subtitle="Review your memory"
          onBack={handleBackToCompose}
          backLabel="Back to compose"
        />
        {respondQuote}

        {/* Review content */}
        <div className={styles.reviewContent}>
          {/* Preview card */}
          <div className={styles.reviewPreview}>
            {title.trim() && (
              <h3 className={styles.reviewTitle}>{title.trim()}</h3>
            )}
            <p className={styles.reviewText}>{text}</p>
          </div>

          {/* Error message */}
          {error && (
            <div className={styles.errorBanner}>{error}</div>
          )}

          {/* Actions */}
          <div className={styles.reviewActions}>
            {(!isEditMode && space?.groupSpacesEnabled) ? (
              <ContributorSaveActions
                saving={saving}
                onSavePrivate={() => handleSave('contributor_only')}
                onShare={(vp, kc) => handleSave(vp, kc)}
                space={space}
                api={access.api}
              />
            ) : (
              <button
                className={styles.btnPrimary}
                onClick={() => handleSave()}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save memory'}
              </button>
            )}
            <button
              className={styles.btnGhost}
              onClick={handleBackToCompose}
              disabled={saving}
            >
              ← Edit
            </button>
          </div>
        </div>

        {/* Saved pill toast — rendered outside other content for fixed positioning */}
        {showSavedPill && <SavedPill />}
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════
     COMPOSE SUB-SCREEN (default)
     ═══════════════════════════════════════════════════ */
  return (
    <div className={styles.composeScreen}>

      {/* v1.3: shared LovedOneBar (was dead WritePage.module.css classes). */}
      <LovedOneBar
        spaceName={spaceName}
        spacePhotoUrl={space.photoUrl}
        subtitle={isEditMode ? 'Edit memory' : 'Write a memory'}
        onBack={handleCancel}
        backLabel="Back to feed"
      />
      {respondQuote}

      {/* v1.3: shared PromptBanner — full-width, OUTSIDE .composeContent to
          match the owner WritePage layout. Was dead WritePage.module.css
          prompt classes. Generic static prompt; still hidden in edit mode. */}
      {!isEditMode && (
        <PromptBanner
          prompt={{ category: 'SHARE A MEMORY', text: `Share a memory of ${spaceName}.` }}
          showSkip={false}
          fullWidth
        />
      )}

      {/* Compose content */}
      <div className={styles.composeContent}>

        {/* Title input */}
        <input
          type="text"
          className={styles.titleInput}
          placeholder="Give it a title (optional)"
          value={title}
          onChange={handleTitleChange}
          maxLength={75}
        />

        {/* Textarea with dictate button */}
        <div className={styles.textareaWrap}>
          <textarea
            ref={textareaRef}
            className={styles.textarea}
            placeholder="Start writing... say whatever comes to mind."
            value={text}
            onChange={handleTextChange}
            maxLength={MAX_CHARS}
          />
          <div className={styles.dictatePosition}>
            <DictateButton onTranscript={handleDictation} size="medium" />
          </div>
        </div>

        {isEditMode && space?.groupSpacesEnabled && (
          <ContributorEditSharing
            visibilityPref={share.visibilityPref}
            reviewStatus={share.reviewStatus}
            groupSpacesEnabled={space?.groupSpacesEnabled}
            saving={sharingBusy}
            onShare={handleShareTier}
            onMakePrivate={handleMakePrivate}
            space={space}
            api={access.api}
            keepCopyExcluded={share.keepCopyExcluded}
            onKeepCopyChange={handleKeepCopy}
          />
        )}
        {sharingNotice && (
          <p style={{ fontSize: 13, color: '#6b6459', margin: '10px 2px 0' }}>
            Your edits to this memory aren&rsquo;t saved yet.
          </p>
        )}

        {/* Footer — edit: compact Cancel/Save changes (saves directly); create: char count + Review */}
        {isEditMode ? (
          <ContributorEditActions
            leftContent={`${charCount.toLocaleString()} / 10,000`}
            onCancel={handleCancel}
            onSave={() => handleSave()}
            saving={saving}
            saveDisabled={!canReview}
          />
        ) : (
          <div className={styles.footer}>
            <span className={`${styles.charCount} ${nearLimit ? styles.nearLimit : ''}`}>
              {charCount.toLocaleString()} / 10,000
            </span>
            <div className={styles.footerButtons}>
              <button className={styles.btnCancel} onClick={handleCancel}>Cancel</button>
              <button className={styles.btnReview} onClick={handleReview} disabled={!canReview}>Review</button>
            </div>
          </div>
        )}
      </div>

      {/* Saved pill toast */}
      {showSavedPill && <SavedPill />}
    </div>
  );
}

/* ═══════════════════════════════════════
   SAVED PILL TOAST
   Inline component — bottom-center, auto-dismisses via parent timer.
   Matches InvitePage v1.1 toast pattern (Session 1B session log):
     - position: fixed, bottom-center
     - role="status" + aria-live="polite"
     - safe-area-inset-bottom respected
     - prefers-reduced-motion disables slide
   Styles inlined here to avoid modifying WritePage.module.css (owner-owned).
   ═══════════════════════════════════════ */

function SavedPill() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
        background: 'var(--color-text-primary, #2d3436)',
        color: '#ffffff',
        padding: '12px 20px',
        borderRadius: '24px',
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: 'var(--font-sans, "DM Sans", -apple-system, sans-serif)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        zIndex: 1000,
        animation: 'none', // Parent controls timing; no slide animation needed.
        pointerEvents: 'none',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          background: 'var(--color-sage, #7c9885)',
          color: '#ffffff',
          fontSize: '11px',
          lineHeight: 1,
        }}
      >
        ✓
      </span>
      Saved
    </div>
  );
}
